import React, { useEffect, useRef, useState } from 'react';
import './ConfirmPasswordModal.css';

/**
 * Asks the user to re-enter their password before a sensitive action.
 * `onConfirm(password)` must reject with an Error to show a validation message.
 */
const ConfirmPasswordModal = ({ title, description, confirmLabel, danger, onConfirm, onClose }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="confirm-backdrop" onMouseDown={() => !submitting && onClose()}>
      <form
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 id="confirm-title">{title}</h2>
        <p className="confirm-description">{description}</p>

        <label htmlFor="confirm-password">Confirm with your password</label>
        <input
          id="confirm-password"
          ref={inputRef}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          className={error ? 'has-error' : ''}
          aria-invalid={!!error}
          aria-describedby={error ? 'confirm-error' : undefined}
          disabled={submitting}
        />
        {error && <p id="confirm-error" className="confirm-error" role="alert">{error}</p>}

        <div className="confirm-actions">
          <button type="button" className="confirm-btn confirm-btn-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            className={`confirm-btn ${danger ? 'confirm-btn-danger' : 'confirm-btn-primary'}`}
            disabled={submitting}
          >
            {submitting ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfirmPasswordModal;
